
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, ArrowLeft, Compass, Palette, Archive, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'aura_onboarding_complete';

interface TourStep {
  id: string;
  title: string;
  description: string;
  targetSelector?: string; // CSS selector for the spotlight target
  icon: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Aura Traveler',
    description:
      'We craft AI-curated luxury travel itineraries tailored to your style, taste, and wanderlust. Each itinerary is a bespoke masterpiece — from itineraries to logistics to visual storytelling.',
    icon: <Sparkles className="w-6 h-6" />,
    position: 'center',
  },
  {
    id: 'destination',
    title: 'Set Your Coordinates',
    description:
      'Start by telling us your dream destination, travel dates, and preferences. Our AI architect will craft a bespoke itinerary — complete with curated restaurants, experiences, and hidden gems.',
    targetSelector: '[data-tour="destination-input"]',
    icon: <Compass className="w-6 h-6" />,
    position: 'bottom',
  },
  {
    id: 'aesthetic',
    title: 'Choose Your Aesthetic',
    description:
      'Select your visual identity — from Editorial Vogue to Hand-Painted Watercolor. Each aesthetic transforms the entire itinerary into a completely different visual experience.',
    targetSelector: '[data-tour="aesthetic-selector"]',
    icon: <Palette className="w-6 h-6" />,
    position: 'top',
  },
  {
    id: 'vault',
    title: 'Your Personal Vault',
    description:
      'Access "The Vault" from your profile menu. It stores all your itineraries — search, filter, share publicly, or email them directly to clients.',
    targetSelector: '[data-tour="user-menu"]',
    icon: <Archive className="w-6 h-6" />,
    position: 'bottom',
  },
  {
    id: 'ready',
    title: "You're All Set",
    description:
      'Pro tips: Use the AI Concierge for real-time recommendations during your trip. Share itineraries to social media with Aura Studio. Create stunning Reels from your trip images.',
    icon: <Sparkles className="w-6 h-6" />,
    position: 'center',
  },
];

interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({});
  const [arrowDirection, setArrowDirection] = useState<'up' | 'down' | 'left' | 'right'>('up');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const resizeTimerRef = useRef<number | null>(null);

  const step = TOUR_STEPS[currentStep];
  const totalSteps = TOUR_STEPS.length;
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;
  const isCentered = !step.targetSelector;

  // Measure and position the spotlight + tooltip
  const updatePosition = useCallback(() => {
    if (!step.targetSelector) {
      setSpotlightRect(null);
      setTooltipStyle({});
      setArrowStyle({ display: 'none' });
      return;
    }

    const el = document.querySelector(step.targetSelector);
    if (!el) {
      // Target not found — fall back to centered
      setSpotlightRect(null);
      setTooltipStyle({});
      setArrowStyle({ display: 'none' });
      return;
    }

    const rect = el.getBoundingClientRect();
    const padding = 12;
    const sr: SpotlightRect = {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    };
    setSpotlightRect(sr);

    // Calculate tooltip position based on available space
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tooltipWidth = Math.min(380, vw - 32);
    const tooltipHeight = 240; // estimated
    const gap = 16;
    const isMobile = vw < 640;

    let style: React.CSSProperties = {};
    let aStyle: React.CSSProperties = {};
    let aDir: 'up' | 'down' | 'left' | 'right' = 'up';

    if (isMobile) {
      // On mobile, always position below the spotlight, full width
      const top = sr.top + sr.height + gap;
      if (top + tooltipHeight > vh) {
        // Not enough space below, put above
        style = {
          bottom: vh - sr.top + gap,
          left: 16,
          right: 16,
        };
        aDir = 'down';
        aStyle = {
          position: 'absolute',
          bottom: -8,
          left: sr.left + sr.width / 2 - 16,
        };
      } else {
        style = {
          top,
          left: 16,
          right: 16,
        };
        aDir = 'up';
        aStyle = {
          position: 'absolute',
          top: -8,
          left: Math.min(Math.max(sr.left + sr.width / 2 - 16, 16), vw - 48),
        };
      }
    } else {
      // Desktop: try preferred position, then fall back
      const preferred = step.position || 'bottom';
      const spaceBelow = vh - (sr.top + sr.height);
      const spaceAbove = sr.top;
      const spaceRight = vw - (sr.left + sr.width);
      const spaceLeft = sr.left;

      if (preferred === 'bottom' && spaceBelow > tooltipHeight + gap) {
        style = {
          top: sr.top + sr.height + gap,
          left: Math.max(16, Math.min(sr.left + sr.width / 2 - tooltipWidth / 2, vw - tooltipWidth - 16)),
          width: tooltipWidth,
        };
        aDir = 'up';
        aStyle = {
          position: 'absolute',
          top: -8,
          left: sr.left + sr.width / 2 - (parseFloat(String(style.left)) || 0) - 6,
        };
      } else if (preferred === 'top' || spaceAbove > tooltipHeight + gap) {
        style = {
          bottom: vh - sr.top + gap,
          left: Math.max(16, Math.min(sr.left + sr.width / 2 - tooltipWidth / 2, vw - tooltipWidth - 16)),
          width: tooltipWidth,
        };
        aDir = 'down';
        aStyle = {
          position: 'absolute',
          bottom: -8,
          left: sr.left + sr.width / 2 - (parseFloat(String(style.left)) || 0) - 6,
        };
      } else if (spaceRight > tooltipWidth + gap) {
        style = {
          top: Math.max(16, sr.top + sr.height / 2 - tooltipHeight / 2),
          left: sr.left + sr.width + gap,
          width: tooltipWidth,
        };
        aDir = 'left';
        aStyle = {
          position: 'absolute',
          left: -8,
          top: sr.top + sr.height / 2 - (parseFloat(String(style.top)) || 0) - 6,
        };
      } else {
        // Fall back to below anyway
        style = {
          top: sr.top + sr.height + gap,
          left: Math.max(16, Math.min(sr.left + sr.width / 2 - tooltipWidth / 2, vw - tooltipWidth - 16)),
          width: tooltipWidth,
        };
        aDir = 'up';
        aStyle = { display: 'none' };
      }
    }

    setTooltipStyle(style);
    setArrowStyle(aStyle);
    setArrowDirection(aDir);
  }, [step]);

  // Scroll target into view and update position
  useEffect(() => {
    if (!isOpen) return;

    if (step.targetSelector) {
      const el = document.querySelector(step.targetSelector);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Wait for scroll to complete before measuring
        const timer = setTimeout(updatePosition, 400);
        return () => clearTimeout(timer);
      }
    }
    updatePosition();
  }, [isOpen, currentStep, updatePosition]);

  // Reposition on resize
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = window.setTimeout(updatePosition, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
    };
  }, [isOpen, updatePosition]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleComplete();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (!isLast) setCurrentStep((s) => s + 1);
        else handleComplete();
      } else if (e.key === 'ArrowLeft') {
        if (!isFirst) setCurrentStep((s) => s - 1);
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isFirst, isLast]);

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setCurrentStep(0);
    onClose();
  };

  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (!isFirst) setCurrentStep((s) => s - 1);
  };

  if (!isOpen) return null;

  // Arrow SVG based on direction
  const ArrowSvg = () => {
    if (arrowStyle.display === 'none') return null;
    const color = '#2A2A2A'; // charcoal tooltip bg
    const size = 12;
    let points = '';
    let svgStyle: React.CSSProperties = { ...arrowStyle, position: 'absolute' as const };

    switch (arrowDirection) {
      case 'up':
        points = `0,${size} ${size / 2},0 ${size},${size}`;
        svgStyle.width = size;
        svgStyle.height = size;
        break;
      case 'down':
        points = `0,0 ${size / 2},${size} ${size},0`;
        svgStyle.width = size;
        svgStyle.height = size;
        break;
      case 'left':
        points = `${size},0 0,${size / 2} ${size},${size}`;
        svgStyle.width = size;
        svgStyle.height = size;
        break;
      case 'right':
        points = `0,0 ${size},${size / 2} 0,${size}`;
        svgStyle.width = size;
        svgStyle.height = size;
        break;
    }

    return (
      <svg style={svgStyle} viewBox={`0 0 ${size} ${size}`}>
        <polygon points={points} fill={color} />
      </svg>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="onboarding-tour-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[9999]"
          style={{ pointerEvents: 'auto' }}
        >
          {/* Dark overlay with spotlight cutout */}
          {isCentered ? (
            // Full overlay with no cutout
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={handleComplete}
            />
          ) : spotlightRect ? (
            <>
              {/* Overlay using box-shadow trick on a transparent element */}
              <div
                className="absolute inset-0"
                onClick={handleComplete}
                style={{ pointerEvents: 'auto' }}
              />
              <div
                className="absolute transition-all duration-500 ease-out"
                style={{
                  top: spotlightRect.top,
                  left: spotlightRect.left,
                  width: spotlightRect.width,
                  height: spotlightRect.height,
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                  borderRadius: '12px',
                  pointerEvents: 'none',
                  zIndex: 9998,
                }}
              />
              {/* Spotlight ring glow */}
              <div
                className="absolute transition-all duration-500 ease-out pointer-events-none"
                style={{
                  top: spotlightRect.top - 2,
                  left: spotlightRect.left - 2,
                  width: spotlightRect.width + 4,
                  height: spotlightRect.height + 4,
                  borderRadius: '14px',
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                  boxShadow: '0 0 20px rgba(212, 175, 55, 0.15)',
                  zIndex: 9999,
                }}
              />
            </>
          ) : (
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={handleComplete}
            />
          )}

          {/* Tooltip Card */}
          <motion.div
            ref={tooltipRef}
            key={`tooltip-${currentStep}`}
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className={`fixed z-[10000] ${isCentered ? 'inset-0 flex items-center justify-center p-4' : ''}`}
            style={isCentered ? {} : { ...tooltipStyle, position: 'fixed' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`
                bg-[#2A2A2A] border border-[#D4AF37]/30 shadow-2xl
                ${isCentered ? 'max-w-md w-full mx-auto' : 'w-full'}
                rounded-sm overflow-hidden
              `}
            >
              {/* Gold accent line at top */}
              <div className="h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

              <div className="p-6 sm:p-8">
                {/* Icon + Title */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
                    {step.icon}
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl text-white tracking-wide leading-tight">
                    {step.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="font-serif text-sm text-gray-300 leading-relaxed mb-6 ml-0 sm:ml-[52px]">
                  {step.description}
                </p>

                {/* Step indicator dots */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      {TOUR_STEPS.map((_, i) => (
                        <div
                          key={i}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            i === currentStep
                              ? 'bg-[#D4AF37] scale-125'
                              : i < currentStep
                              ? 'bg-[#D4AF37]/40'
                              : 'bg-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="font-sans text-[10px] uppercase tracking-widest text-gray-500">
                      {currentStep + 1} of {totalSteps}
                    </span>
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex items-center gap-2">
                    {!isFirst && (
                      <button
                        onClick={handleBack}
                        className="flex items-center justify-center w-11 h-11 sm:w-10 sm:h-10 rounded-sm border border-gray-600 text-gray-400 hover:text-white hover:border-gray-400 transition-colors"
                        aria-label="Previous step"
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={handleNext}
                      className="flex items-center justify-center gap-2 h-11 sm:h-10 px-5 rounded-sm bg-[#D4AF37] text-[#1a1a1a] font-sans text-[11px] uppercase tracking-widest font-bold hover:bg-[#d4b868] transition-colors min-w-[44px]"
                    >
                      {isFirst ? (
                        "Let's Go"
                      ) : isLast ? (
                        'Start Exploring'
                      ) : (
                        <>
                          Next
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Skip link */}
                {!isLast && (
                  <div className="mt-4 text-center ml-[52px]">
                    <button
                      onClick={handleComplete}
                      className="font-sans text-[10px] uppercase tracking-widest text-gray-500 hover:text-[#D4AF37] transition-colors"
                    >
                      Skip Tour
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Arrow pointer (only for non-centered steps) */}
            {!isCentered && <ArrowSvg />}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Helper to check if the tour has been completed
export const isOnboardingComplete = (): boolean => {
  return localStorage.getItem(STORAGE_KEY) === 'true';
};

// Helper to reset the tour (for re-triggering)
export const resetOnboarding = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export default OnboardingTour;
